import { useState, useRef, useCallback, useMemo } from "react";
import { Outlet, useOutletContext, useParams, useNavigate, useLocation, NavLink } from "react-router";
import { Virtuoso } from "react-virtuoso";
import { Search, MessageSquare, Settings, Grid3x3, SquarePen, Pin, Archive, ArchiveRestore, BellOff, Bell } from "lucide-react";
import * as ContextMenu from "@radix-ui/react-context-menu";
import { cn } from "../../lib/utils";
import { useChats, isInvite, href } from "../../util";
import type { Chat } from "../../lib/xmpp";
import { useSettingsStore } from "../../stores/settingsStore";
import { useChatFlagsStore } from "../../stores/chatFlagsStore";
import { DialPad } from "../call/DialPad";
import { useKeyboardShortcuts } from "../../hooks/useKeyboardShortcuts";

const PresenceDot = ({ presence }: { presence?: string }) => {
	const colors: Record<string, string> = {
		online: "bg-green-400",
		away: "bg-yellow-400",
		dnd: "bg-red-400",
		offline: "bg-gray-500",
	};
	if (!presence || !colors[presence]) return null;
	return (
		<span
			className={cn(
				"absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-[#0d0d10]",
				colors[presence],
			)}
		/>
	);
};

const SectionHeader = ({ label }: { label: string }) => (
	<div className="px-4 pt-3 pb-1.5 flex items-center gap-2">
		<span className="text-[11px] font-semibold uppercase tracking-wider text-white/35">
			{label}
		</span>
		<div className="flex-1 h-px bg-white/8" />
	</div>
);

const ConversationItem = ({
	chat,
	isActive,
	onClick,
	accountId,
}: {
	chat: Chat;
	isActive: boolean;
	onClick: () => void;
	accountId: string;
}) => {
	const presenceStr = chat.getParticipantPresence(chat.chatId)?.state;
	const unread = chat.unreadCount;
	const lastMsg = chat.lastMessage;
	const name = chat.displayName;
	const initials = name
		.split(" ")
		.map((w: string) => w[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();

	const setArchived = useChatFlagsStore((s) => s.setArchived);
	const setMuted = useChatFlagsStore((s) => s.setMuted);
	const archived = useChatFlagsStore((s) => s.isArchived(accountId, chat.chatId));
	const muted = useChatFlagsStore((s) => s.isMuted(accountId, chat.chatId));

	const handleTogglePin = useCallback(() => {
		chat.setBookmarked(!chat.isBookmarked);
	}, [chat]);
	const handleToggleArchive = useCallback(() => {
		setArchived(accountId, chat.chatId, !archived);
	}, [accountId, chat.chatId, archived, setArchived]);
	const handleToggleMute = useCallback(() => {
		setMuted(accountId, chat.chatId, !muted);
	}, [accountId, chat.chatId, muted, setMuted]);

	return (
		<ContextMenu.Root>
			<ContextMenu.Trigger asChild>
				<button
					onClick={onClick}
					className={cn(
						"flex items-center gap-3 px-4 py-3 w-full text-left transition-all hover:bg-white/5",
						isActive
							? "bg-white/10 border-l-2 border-primary"
							: "border-l-2 border-transparent",
					)}
				>
					{/* Avatar */}
					<div className="relative shrink-0">
						<div className={cn(
							"w-10 h-10 rounded-full bg-primary/50 flex items-center justify-center text-white text-sm font-medium",
							muted && "opacity-50",
						)}>
							{initials}
						</div>
						<PresenceDot presence={presenceStr} />
					</div>

					{/* Content */}
					<div className="flex-1 min-w-0">
						<div className="flex items-center justify-between">
							<span className={cn(
								"text-sm font-medium truncate",
								muted ? "text-white/55" : "text-white/90",
							)}>{name}</span>
							<span className="flex items-center gap-1 ml-2 shrink-0">
								{muted && <BellOff size={12} className="text-white/40" />}
								{chat.isBookmarked && (
									<Pin size={12} className="text-white/40" />
								)}
								{lastMsg?.timestamp && (
									<span className="text-xs text-white/40">
										{new Date(lastMsg.timestamp).toLocaleTimeString([], {
											hour: "2-digit",
											minute: "2-digit",
										})}
									</span>
								)}
							</span>
						</div>
						<div className="flex items-center justify-between mt-0.5">
							<span className="text-xs text-white/50 truncate">
								{lastMsg?.text ?? chat.chatId}
							</span>
							{unread > 0 && !muted && (
								<span className="ml-2 shrink-0 min-w-[1.25rem] h-5 px-1.5 rounded-full bg-primary text-white text-xs flex items-center justify-center font-medium">
									{unread > 99 ? "99+" : unread}
								</span>
							)}
							{unread > 0 && muted && (
								<span className="ml-2 shrink-0 min-w-[1.25rem] h-5 px-1.5 rounded-full bg-white/10 text-white/55 text-xs flex items-center justify-center font-medium">
									{unread > 99 ? "99+" : unread}
								</span>
							)}
						</div>
					</div>
				</button>
			</ContextMenu.Trigger>

			<ContextMenu.Portal>
				<ContextMenu.Content className="bg-[#1a1a24] border border-white/12 rounded-xl shadow-2xl p-1 z-50 min-w-[180px]">
					<ContextMenu.Item
						className="px-3 py-2 text-sm text-white/80 hover:bg-white/8 rounded-lg cursor-pointer outline-none flex items-center gap-2"
						onSelect={handleTogglePin}
					>
						<Pin size={14} />
						{chat.isBookmarked ? "Unpin Chat" : "Pin Chat"}
					</ContextMenu.Item>
					<ContextMenu.Item
						className="px-3 py-2 text-sm text-white/80 hover:bg-white/8 rounded-lg cursor-pointer outline-none flex items-center gap-2"
						onSelect={handleToggleArchive}
					>
						{archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
						{archived ? "Unarchive Chat" : "Archive Chat"}
					</ContextMenu.Item>
					<ContextMenu.Item
						className="px-3 py-2 text-sm text-white/80 hover:bg-white/8 rounded-lg cursor-pointer outline-none flex items-center gap-2"
						onSelect={handleToggleMute}
					>
						{muted ? <Bell size={14} /> : <BellOff size={14} />}
						{muted ? "Unmute Notifications" : "Mute Notifications"}
					</ContextMenu.Item>
				</ContextMenu.Content>
			</ContextMenu.Portal>
		</ContextMenu.Root>
	);
};

export const Chats = () => {
	const ctx = useOutletContext<any>();
	const params = useParams();
	const navigate = useNavigate();
	const { messageRequests } = useSettingsStore();
	const [search, setSearch] = useState("");
	const [dialOpen, setDialOpen] = useState(false);
	const [composeOpen, setComposeOpen] = useState(false);
	const [composeJid, setComposeJid] = useState("");
	const searchRef = useRef<HTMLInputElement>(null);
	const desktopSearchRef = useRef<HTMLInputElement>(null);
	const composeRef = useRef<HTMLInputElement>(null);

	const { client, accounts, persistence } = ctx;
	const accountId = params.accountId!;
	const archivedMap = useChatFlagsStore((s) => s.archived);
	const [showArchived, setShowArchived] = useState(false);

	const allChats = useChats(client);
	const chats = allChats.filter((c) => !isInvite(messageRequests, c) && !c.isApp);
	const invites = allChats.filter((c) => isInvite(messageRequests, c));

	// Split archived vs active. Searching overrides archive filtering so users can find buried chats.
	const isArchivedFor = (chatId: string) => !!archivedMap[`${accountId}\x00${chatId}`];
	const archivedChats = chats.filter((c) => isArchivedFor(c.chatId));
	const activeChats = chats.filter((c) => !isArchivedFor(c.chatId));

	const baseList = search ? chats : activeChats;
	const filtered = search
		? baseList.filter((c) =>
				c.displayName.toLowerCase().includes(search.toLowerCase()),
			)
		: baseList;

	const pinned = filtered.filter((c) => c.isBookmarked);
	const unpinned = filtered.filter((c) => !c.isBookmarked);
	const sorted = [...pinned, ...unpinned];

	const activeChatId = params.chatId;
	const location = useLocation();
	const isAppsRoute = location.pathname.includes("/apps");

	const onMobile = window.innerWidth < 768;
	const showSidebar = !isAppsRoute && (!onMobile || !activeChatId);

	const goToChat = (chatId: string) =>
		navigate(href("/accounts/:accountId/chats/:chatId", { accountId, chatId }), {
			replace: false,
		});

	const submitCompose = (e: React.FormEvent) => {
		e.preventDefault();
		const jid = composeJid.trim();
		if (!jid) return;
		setComposeOpen(false);
		setComposeJid("");
		goToChat(jid);
	};

	const openCompose = () => {
		setComposeJid("");
		setComposeOpen((v) => !v);
	};

	const shortcuts = useMemo(
		() => ({
			"mod+k": () => {
				const onMobileNow = window.innerWidth < 768;
				if (onMobileNow) {
					searchRef.current?.focus();
				} else {
					desktopSearchRef.current?.focus();
				}
			},
			"mod+n": () => {
				openCompose();
			},
			escape: () => {
				if (composeOpen) setComposeOpen(false);
				if (dialOpen) setDialOpen(false);
			},
			"alt+up": () => {
				if (!activeChatId || sorted.length === 0) return;
				const idx = sorted.findIndex((c) => c.chatId === activeChatId);
				if (idx > 0) goToChat(sorted[idx - 1].chatId);
			},
			"alt+down": () => {
				if (!activeChatId || sorted.length === 0) return;
				const idx = sorted.findIndex((c) => c.chatId === activeChatId);
				if (idx < sorted.length - 1) goToChat(sorted[idx + 1].chatId);
			},
		}),
		[composeOpen, dialOpen, activeChatId, sorted],
	);

	useKeyboardShortcuts(shortcuts);

	return (
		<div className="flex h-full">
			{/* Conversation list sidebar */}
			<aside
				className={cn(
					"relative flex flex-col glass-panel border-r border-white/8",
					"w-full md:w-72 lg:w-80 shrink-0",
					!showSidebar && "hidden md:flex",
				)}
			>
				{/* Mobile header — account name + compose + settings */}
				<div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-white/8 shrink-0">
					<span className="text-sm font-semibold text-white">Messages</span>
					<div className="flex items-center gap-1">
						<button
							onClick={openCompose}
							className="w-8 h-8 rounded-lg flex items-center justify-center text-white/55 hover:text-white hover:bg-white/10 transition-all"
							title="New conversation"
						>
							<SquarePen size={18} />
						</button>
						<NavLink
							to="/settings"
							target="_self"
							className="w-8 h-8 rounded-lg flex items-center justify-center text-white/55 hover:text-white hover:bg-white/10 transition-all"
							title="Settings"
						>
							<Settings size={18} />
						</NavLink>
					</div>
				</div>

				{/* Desktop header — "Messages" + compose button */}
				<div className="hidden md:flex items-center justify-between px-4 py-3 border-b border-white/8 shrink-0">
					<span className="text-sm font-semibold text-white">Messages</span>
					<div className="relative">
						<button
							onClick={openCompose}
							className="w-8 h-8 rounded-lg flex items-center justify-center text-white/55 hover:text-white hover:bg-white/10 transition-all"
							title="New conversation"
						>
							<SquarePen size={18} />
						</button>
						{composeOpen && (
							<div className="absolute right-0 top-10 w-64 rounded-xl bg-[#1a1a24] border border-white/12 shadow-2xl z-50 overflow-hidden">
								<form onSubmit={submitCompose} className="p-2">
									<div className="relative">
										<Search
											size={14}
											className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"
										/>
										<input
											ref={composeRef}
											autoFocus
											value={composeJid}
											onChange={(e) => setComposeJid(e.target.value)}
											onBlur={() => setTimeout(() => setComposeOpen(false), 150)}
											placeholder="Search by name, ID"
											className="w-full pl-8 pr-4 py-2 rounded-lg bg-white/8 border border-white/8 text-sm text-white placeholder-white/40 focus:outline-none focus:border-white/20 transition-colors"
										/>
									</div>
								</form>
							</div>
						)}
					</div>
				</div>

				{/* Desktop search bar */}
				<div className="hidden md:block px-3 pb-3 shrink-0">
					<div className="relative">
						<Search
							size={16}
							className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
						/>
						<input
							ref={desktopSearchRef}
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search conversations..."
							className="w-full pl-9 pr-4 py-2 rounded-xl bg-white/8 border border-white/8 text-base text-white placeholder-white/40 focus:outline-none focus:border-white/20 transition-colors"
						/>
					</div>
				</div>

				{/* Mobile compose input (shown inline when composeOpen on mobile) */}
				{composeOpen && (
					<div className="md:hidden px-3 pb-3 shrink-0">
						<form onSubmit={submitCompose}>
							<div className="relative">
								<Search
									size={15}
									className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"
								/>
								<input
									autoFocus
									value={composeJid}
									onChange={(e) => setComposeJid(e.target.value)}
									onBlur={() => setTimeout(() => setComposeOpen(false), 150)}
									placeholder="Search by name, ID"
									className="w-full pl-9 pr-4 py-2 rounded-xl bg-white/8 border border-white/8 text-base text-white placeholder-white/40 focus:outline-none focus:border-white/20 transition-colors"
								/>
							</div>
						</form>
					</div>
				)}

				{/* Invites badge */}
				{invites.length > 0 && !search && (
					<button
						onClick={() => navigate(href("/accounts/:accountId/requests", { accountId }))}
						className="mx-3 mt-3 px-4 py-2.5 rounded-xl bg-primary/20 border border-primary/30 text-sm text-primary-light hover:bg-primary/30 transition-colors text-left shrink-0"
					>
						{invites.length} message request{invites.length > 1 ? "s" : ""}
					</button>
				)}

				{/* Chat list */}
				<div className="flex-1 overflow-hidden">
					{sorted.length === 0 ? (
						<div className="flex flex-col items-center justify-center h-full gap-3 text-white/40 px-6 text-center">
							<MessageSquare size={40} strokeWidth={1} />
							<p className="text-sm">
								{search
									? "No conversations match your search"
									: "No conversations yet"}
							</p>
						</div>
					) : (
						<div className="h-full flex flex-col">
							<div className="flex-1">
								<Virtuoso
									style={{ height: "100%" }}
									data={sorted}
									itemContent={(index, chat) => (
										<>
											{pinned.length > 0 && index === 0 && (
												<SectionHeader label="Pinned" />
											)}
											{unpinned.length > 0 && index === pinned.length && (
												<SectionHeader label="Recent" />
											)}
											<ConversationItem
												key={chat.chatId}
												chat={chat}
												isActive={chat.chatId === activeChatId}
												onClick={() => goToChat(chat.chatId)}
												accountId={accountId}
											/>
										</>
									)}
								/>
							</div>
							{!search && archivedChats.length > 0 && (
								<div className="shrink-0 border-t border-white/8">
									<button
										onClick={() => setShowArchived((v) => !v)}
										className="w-full px-4 py-2 flex items-center gap-2 text-xs uppercase tracking-wider text-white/40 hover:text-white/60 hover:bg-white/5 transition-colors"
									>
										<Archive size={12} />
										<span>Archived ({archivedChats.length})</span>
										<span className="ml-auto">{showArchived ? "▾" : "▸"}</span>
									</button>
									{showArchived && (
										<div className="max-h-72 overflow-y-auto">
											{archivedChats.map((chat) => (
												<ConversationItem
													key={chat.chatId}
													chat={chat}
													isActive={chat.chatId === activeChatId}
													onClick={() => goToChat(chat.chatId)}
													accountId={accountId}
												/>
											))}
										</div>
									)}
								</div>
							)}
						</div>
					)}
				</div>

				{/* Mobile bottom bar — Liquid Glass floating pill */}
				<div
					className={cn("md:hidden shrink-0 px-3 pt-2", activeChatId && "hidden")}
					style={{ paddingBottom: "calc(max(0.75rem, env(safe-area-inset-bottom)) + 0.25rem)" }}
				>
					<div className="flex items-center gap-2 px-3 py-2 rounded-2xl glass-liquid">
						<div className="relative flex-1">
							<Search
								size={15}
								className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"
							/>
							<input
								ref={searchRef}
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder="Search…"
								className="w-full pl-9 pr-4 py-2 rounded-xl bg-white/6 border border-white/10 text-base text-white placeholder-white/35 focus:outline-none focus:border-white/25 focus:bg-white/10 transition-colors"
							/>
						</div>
						<button
							onClick={() => setDialOpen(true)}
							className="w-9 h-9 rounded-full glass-raised flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-colors shrink-0"
							title="Dial pad"
						>
							<Grid3x3 size={18} />
						</button>
					</div>
				</div>

				{/* Dial pad — overlays the full sidebar (aside is position:relative) */}
				{dialOpen && (
					<DialPad
						accountId={accountId}
						client={client}
						onClose={() => setDialOpen(false)}
					/>
				)}
			</aside>

			{/* Chat view */}
			<main className={cn("flex-1 overflow-hidden", !isAppsRoute && !activeChatId && "hidden md:block")}>
				<Outlet context={{ ...ctx, accountId }} />
			</main>
		</div>
	);
};
